import React, { useEffect, useState, useRef } from 'react';
import { Modal, Spin } from 'antd';
import DataTable from 'react-data-table-component';
import 'antd/dist/antd.css';
import { useHistory } from "react-router-dom";
import { ADMIN_AADHAR_PENDING_KYC_USERS, DIGILOCKER_VERIFY,
    CREATE_DIGILOCKER_URL,
    GET_VERIFICATION_STATUS,
    GET_DOCUMENT_DATA
 } from '../../../Utils/appConstants';
import { getRequest, postRequest } from '../../../Utils/AppApi';
import { ToastContainer, toast } from 'react-toastify';
import { kycStatusValue, getUser, updateUserSession } from '../../../Utils/Common';
import { getUTCToLocalDateFormat } from '../../../Utils/function';

function App(props) {
    const user = getUser();
    const history = useHistory();
    const limit = 10;
    const search = props.search ? props.search : "";
    const kycStatus = props.kycStatus ? props.kycStatus : "";

    const [getData, setData] = useState([]);
    const [getTotalRecord, setTotalRecord] = useState(0);
    const [page, setPage] = useState(1);
    const [isSelectedUser, setIsSelectedUser] = useState([]);
    
    const [isAadharCardVerify, setIsAadharCardVerify] = useState(false);
    const [aadharNumber, setAadharNumber] = useState('');
    const [isLoading, setLoading] = useState(false);
    
    const verificationIdRef = useRef(null);
    const referenceIdRef = useRef(null);
    const enteredAadhaarRef = useRef('');

    const columns = [{
        name: 'User Id',
        selector: row => row.user.username,
    }, {
        name: 'Pan No.',
        selector: row => row.pancardNumber,
    }, {
        name: 'Name',
        selector: row => row.user.name,
    }, {
        name: 'Mobile',
        selector: row => row.mobile,
    }, {
        name: 'User Type',
        selector: row => row.role.name,
    }, {
        name: 'Parent ID',
        selector: row => <>{row.userParentDetail && row.userParentDetail.username}</>,
    }, {
        name: 'KYC Status',
        cell: row => <>{kycStatusValue(row.user.KYCStatus)}</>,
    }, {
        name: 'Active',
        cell: row => <>{(row.user.active) ? <div className="text-success">Active</div> : <div className="text-danger">Deactive</div>}</>,
    }, {
        name: 'Date',
        cell: row => getUTCToLocalDateFormat(row.createdAt),
    }, {
        name: 'Action',
        cell: row => <>{!row.aadharNumberVerify && <div className='icon-border border border-danger text-danger pointer' onClick={(event) => handleAadhaar(event, row)}><i class="fa fa-info-circle" aria-hidden="true"></i></div>}</>,
    }]

    const handleAadhaar = (event, data) => {
        setIsAadharCardVerify(true);
        setIsSelectedUser(data)
        setAadharNumber(data.aadharNumber || '');
    }

    useEffect(() => {
        const handleFocus = async () => {
            const vid = verificationIdRef.current;
            const refId = referenceIdRef.current;

            if (!vid) return;

            try {
                setLoading(true);
                const payload = {
                    verificationId: vid,
                    referenceId: refId,
                };

                const res = await postRequest(GET_VERIFICATION_STATUS, payload, history);
                console.log('Check DigiLocker Document Response:', res);

                if (res && res.success) {
                    await getDocumentData(vid, refId, 'AADHAAR');
                } else {
                    toast.error(res?.message || 'Document verification failed');
                }
            } catch (err) {
                console.error('Error checking DigiLocker docs:', err);
                toast.error('Error verifying documents');
            } finally {
                setLoading(false);
            }
        };

        window.addEventListener('focus', handleFocus);
        return () => {
            window.removeEventListener('focus', handleFocus);
        };
    }, [history]);

    const initiateDigilocker = () => {
        if (!aadharNumber || aadharNumber.length !== 12) {
            toast.error('Please enter a valid 12-digit Aadhaar number');
            return;
        }

        enteredAadhaarRef.current = aadharNumber;

        setLoading(true);
        const payload = {
            aadharNumber: aadharNumber,
            type: 'user_kyc',
        };
        
        postRequest(DIGILOCKER_VERIFY, payload, history)
            .then((response) => {
                console.log('DigiLocker Response:', response);
                if (response.success === true) {
                    const verification_id = response.data?.verification_id;
                    if (verification_id) {
                        createDigilockerURL(verification_id);
                    } else {
                        toast.error('Missing verification id from DigiLocker response');
                    }
                } else {
                    toast.error(response.message || 'Failed to initiate DigiLocker');
                }
            })
            .catch((error) => {
                console.error('DigiLocker Error:', error);
                toast.error('Failed to initiate DigiLocker verification');
            })
            .finally(() => setLoading(false));
    };

    const createDigilockerURL = (verification_id) => {
        setLoading(true);
        const payload = {
            verification_id,
            type: 'onboarding',
        };
        
        postRequest(CREATE_DIGILOCKER_URL, payload, history)
            .then((response) => {
                console.log('DigiLocker Response:', response);
                if (response.success === true && response.data) {
                    verificationIdRef.current = response.data.verification_id;
                    referenceIdRef.current = response.data.reference_id;
                    
                    if (response.data.url) {
                        window.open(response.data.url, '_blank', 'noopener,noreferrer');
                    }
                } else {
                    toast.error(response.message || 'Failed to create DigiLocker URL');
                }
            })
            .catch((error) => {
                console.error('DigiLocker Error:', error);
                toast.error('Failed to initiate DigiLocker verification');
            })
            .finally(() => setLoading(false));
    };

    const updateUserAadhaarViaDigilocker = async (digilockerData) => {
        try {
            // Use the Aadhaar number from the ref (captured during initiateDigilocker)
            const actualAadharNumber = enteredAadhaarRef.current || aadharNumber;
            
            if (!actualAadharNumber || actualAadharNumber.length !== 12) {
                toast.error('Aadhaar number is missing or invalid');
                return false;
            }

            const payload = {
                userKycId: isSelectedUser.userId,
                aadharData: digilockerData, // Send the complete DigiLocker response
                aadharNumber: actualAadharNumber // Make sure this is populated
            };
            
            console.log('Sending to backend:', payload);
            
            const response = await postRequest('/users/update-aadhaar-via-digilocker', payload, history);
            console.log('Backend response:', response);
            
            if (response.success === true || response.alreadysuccess === true) {
                // Update admin user session to reflect successful verification (same as original OTP flow)
                if (user && user.data && user.data.userDetails) {
                    user.data.userDetails.aadharNumberVerify = true;
                    if (updateUserSession) {
                        updateUserSession(user);
                    }
                }
                return true;
            } else {
                toast.error(response.message || 'Failed to update Aadhaar verification');
                return false;
            }
        } catch (error) {
            console.error('Error updating Aadhaar via DigiLocker:', error);
            toast.error('Failed to update Aadhaar verification');
            return false;
        }
    };

    const getDocumentData = async (vid, refId, documentType) => {
        if (!vid) return;
        try {
            setLoading(true);
            const payload = {
                verificationId: vid,
                referenceId: refId,
                documentType: documentType,
            };
            
            const res = await postRequest(GET_DOCUMENT_DATA, payload, history);
            console.log('Get Document Data Response:', res);
            
            if (res && res.success) {
                if (documentType === 'AADHAAR') {
                    if (res.data && res.data.uid) {
                        const enteredAadhaar = enteredAadhaarRef.current || aadharNumber || '';
                        const maskedAadhaar = res?.data?.uid?.trim() || '';
                        
                        console.log('Aadhaar Validation - Stored:', enteredAadhaar, 'From API:', maskedAadhaar);
                        
                        if (!enteredAadhaar) {
                            toast.error('Aadhaar number is missing for validation. Please re-enter the Aadhaar number.');
                            return;
                        }
                        
                        const aadhaarRegex = /^\d{12}$/;
                        if (!aadhaarRegex.test(enteredAadhaar)) {
                            toast.error('Please enter a valid 12-digit Aadhaar Number');
                            return;
                        }
                        
                        const maskedLast4 = maskedAadhaar.slice(-4);
                        const enteredLast4 = enteredAadhaar.slice(-4);
                        
                        console.log('Aadhaar Last 4 comparison - Entered:', enteredLast4, 'API:', maskedLast4);
                        
                        if (maskedLast4 !== enteredLast4) {
                            toast.error('Aadhaar number does not match the provided record');
                            return;
                        }
                        
                        // Update user verification via backend API
                        const updateSuccess = await updateUserAadhaarViaDigilocker(res.data);
                        
                        if (updateSuccess) {
                            setIsAadharCardVerify(false);
                            
                            // Reset refs to null after successful verification
                            verificationIdRef.current = null;
                            referenceIdRef.current = null;
                            enteredAadhaarRef.current = '';
                            
                            toast.success('Aadhaar verified successfully');
                            getDataReports();
                        }else{
                              verificationIdRef.current = null;
                            referenceIdRef.current = null;
                            enteredAadhaarRef.current = '';
                        }
                    } else {
                        toast.error('No Aadhaar data found in the response');
                          verificationIdRef.current = null;
                            referenceIdRef.current = null;
                            enteredAadhaarRef.current = '';
                    }
                }
            } else {
                toast.error(res?.message || 'Document verification failed');
                  verificationIdRef.current = null;
                            referenceIdRef.current = null;
                            enteredAadhaarRef.current = '';
            }
        } catch (err) {
            console.error('Error checking DigiLocker docs:', err);
            toast.error('Error verifying documents');
        } finally {
            setLoading(false);
        }
    };

    const objectToQueryString = (obj) => {
        var str = [];
        for (var p in obj)
            if (obj.hasOwnProperty(p)) {
                str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
            }
        return str.join("&");
    }

    const params = { page: page, limit: limit };

    const getDataReports = () => {
        if (kycStatus) {
            params.isStatus = kycStatus;
        }

        if (search) {
            params.search = search ? search : "";
        }

        const queryString = objectToQueryString(params);
        getRequest(`${ADMIN_AADHAR_PENDING_KYC_USERS}?${queryString}`, history).then((response) => {
            if (response.success === true) {
                setData(response.data);
                setTotalRecord(response.data.totalDocs);
                setPage(response.data.page);
            } else {
                setData([]);
                setTotalRecord(0);
                setPage(1);
            }
        }).catch(function (error) {
            // console.log(error)
            // setLoading(false);
        })
    }

    const handlePageChange = page => {
        setPage(page);
    };

    useEffect(() => {
        if (props.handleSearch) {
            getDataReports();
        }
        if(!props.handleSearch) {
            setData([])
        }
    }, [page, props.handleSearch]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        getDataReports();
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div>
            {isLoading && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        background: 'white',
                        padding: '20px',
                        borderRadius: '5px',
                        textAlign: 'center'
                    }}>
                        <Spin size="large" />
                        <div style={{ marginTop: '10px' }}>Verifying Aadhaar...</div>
                    </div>
                </div>
            )}
            
            <div className="container-fluid pt-2 pb-5">
                <div className="col-md-12">
                    <div className="box-body">
                        <DataTable
                            title={""}
                            columns={columns}
                            data={getData.docs}
                            highlightOnHover
                            pagination
                            paginationServer
                            paginationTotalRows={getTotalRecord}
                            onChangeRowsPerPage={getDataReports}
                            onChangePage={handlePageChange}
                        />
                    </div>
                </div>
            </div>
            
            <ToastContainer position="top-center" autoClose={5000} limit={2} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="colored" />
            
            {isAadharCardVerify && (
                <Modal
                    title="Aadhar Card Verification"
                    centered
                    open={isAadharCardVerify}
                    onCancel={() => { if (!isLoading) setIsAadharCardVerify(false); }}
                    onOk={() => initiateDigilocker()}
                    confirmLoading={isLoading}
                    closable={!isLoading}
                    maskClosable={!isLoading}
                    okText="Verify via DigiLocker"
                    cancelText="Cancel"
                >
                    <ul className='mb-3'>
                        <li><span>User ID</span> : {isSelectedUser.user.username}</li>
                        <li><span>Name</span> : {isSelectedUser.user.name}</li>
                        <li><span>Pancard Number</span> : {isSelectedUser.pancardNumber}</li>
                        <li><span>Pancard Name</span> : {isSelectedUser.pancardNumberVerifiedDetail.registered_name}</li>
                    </ul>
                    <div className="input-group">
                        <div className="custom-file">
                            <input 
                                type="text" 
                                className="form-control" 
                                name="aadharNumber" 
                                placeholder="Aadharcard Number" 
                                value={aadharNumber} 
                                maxLength={12} 
                                // readOnly={isSelectedUser && isSelectedUser.aadharNumber} 
                                onChange={(e) => setAadharNumber(e.target.value.replace(/[^0-9]/g, ''))} 
                            />
                        </div>
                    </div>
                    <hr />
                    {/* <div className="alert alert-info">
                        <small>Click "Verify via DigiLocker" to verify Aadhaar using DigiLocker service.</small>
                    </div> */}
                </Modal>
            )}
        </div>
    )
}

export default App